import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useIsPlatformAdmin } from '../../../hooks/useAuth'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { Button } from '../../../components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table'
import { EmptyState } from '../../../components/ui/empty-state'
import { formatBillingValue, useAdminBillingUsage } from '../../../hooks/use-admin-billing'

export const Route = createFileRoute('/admin/billing/')({
  component: RouteComponent,
})

// Gated with useIsPlatformAdmin, like /admin/usage: a cross-org surface outside
// any $org context. Read-only (spec 2026-10-05-12).
function RouteComponent() {
  const { t, i18n } = useTranslation('admin')
  const { loading, orgCode } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  const navigate = useNavigate({ from: '/admin/billing/' })

  useEffect(() => {
    if (!loading && !isPlatformAdmin) {
      navigate({ to: '/', replace: true })
    }
  }, [loading, isPlatformAdmin, navigate])

  const breadcrumbOrg = orgCode || 'demo'
  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${breadcrumbOrg}` },
    { title: t('settings:page.title'), path: `/${breadcrumbOrg}/settings` },
    { title: t('billing.title') },
  ])

  const { data, isLoading, error } = useAdminBillingUsage()
  const metrics = data?.metrics ?? []
  const rows = data?.data ?? []
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en'

  if (loading || !isPlatformAdmin) {
    return null
  }

  return (
    <div className="space-y-6" data-testid="admin-billing-page">
      <PageHeader
        icon={SECTION_ICONS.adminBilling}
        title={t('billing.title')}
        description={t('billing.subtitle')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/admin/billing/invoices" data-testid="billing-invoices-link">
                {t('billing.invoices.title')}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/billing/simulator" data-testid="billing-simulator-link">
                {t('billing.simulator.link')}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/admin/billing/prices" data-testid="billing-prices-link">
                {t('billing.prices.list_title')}
              </Link>
            </Button>
          </div>
        }
      />

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 text-destructive text-sm" data-testid="billing-error">
          {t('billing.load_error')}
        </div>
      )}

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-4 text-center text-muted-foreground">{t('billing.loading')}</div>
        ) : rows.length === 0 ? (
          <EmptyState variant="inline" live className="py-8" title={t('billing.empty')} data-testid="billing-empty" />
        ) : (
          <Table data-testid="billing-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.column.organization')}</TableHead>
                <TableHead>{t('billing.column.day')}</TableHead>
                {metrics.map((m) => (
                  <TableHead key={m.code} className="text-right" data-testid={`billing-head-${m.code}`}>
                    {m.label[lang] ?? m.label.en}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.organization_uid}
                  data-testid={`billing-row-${row.organization_code}`}
                  className="cursor-pointer"
                  onClick={() => navigate({ to: '/admin/billing/$orgUid', params: { orgUid: row.organization_uid } })}
                >
                  <TableCell>
                    <Link
                      to="/admin/billing/$orgUid"
                      params={{ orgUid: row.organization_uid }}
                      className="text-foreground hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.organization_name || row.organization_code}
                    </Link>
                    <div className="text-xs font-mono text-muted-foreground">{row.organization_code}</div>
                  </TableCell>
                  <TableCell className="text-muted-foreground" data-testid={`billing-day-${row.organization_code}`}>
                    {row.day ?? t('billing.never')}
                  </TableCell>
                  {metrics.map((m) => (
                    <TableCell
                      key={m.code}
                      className="text-right tabular-nums"
                      data-testid={`billing-${row.organization_code}-${m.code}`}
                    >
                      {formatBillingValue(row.metrics[m.code], m.unit, i18n.language)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <p className="text-xs text-muted-foreground">{t('billing.legend')}</p>
    </div>
  )
}
